import { useEffect, useRef } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch, apiJson, ApiError } from '@/lib/apiClient';
import { isNotFound } from '@/features/trips/useTrips';
import type { AlertsResponse, AlertFilter } from './types';

/**
 * The feed filter, resolved to the two independent wire params it maps to. `alerts`
 * is SERVER-SIDE (`?severity=alert`) — it must not be a client filter over a keyset
 * page (that would show fewer rows than a page and break `hasMore`). `unread` maps
 * to `?unreadOnly=true`. `all` sends neither.
 */
export interface AlertQueryOpts {
  unreadOnly: boolean;
  /** Set to `'alert'` for the `Alerts` chip; omitted otherwise. */
  severity?: 'alert';
}

export function optsForFilter(filter: AlertFilter): AlertQueryOpts {
  return {
    unreadOnly: filter === 'unread',
    ...(filter === 'alerts' ? { severity: 'alert' as const } : {}),
  };
}

/**
 * The alerts endpoints (PLAN §4), consumed exactly as the wire contract defines
 * them (S1). Mirrors `useTrips.ts`: `isNotFound` reused, and a 404/400 is a
 * definitive answer about a vehicle, not a transient failure, so it is not retried.
 *
 * The per-vehicle route 404s a foreign/nonexistent id byte-identically; the fleet
 * route returns an EMPTY list for a foreign `vehicleId` (a 404 there would make the
 * filter a probe). Both are handled by the server — the client just consumes.
 */

export const fleetAlertsQueryKey = (
  from: string,
  to: string,
  vehicleId: string | null,
  opts: AlertQueryOpts,
) => ['alerts', 'fleet', from, to, vehicleId ?? 'all', opts.unreadOnly, opts.severity ?? 'any'] as const;

export function useFleetAlerts(
  range: { from: string; to: string },
  vehicleId: string | null,
  opts: AlertQueryOpts,
): UseQueryResult<AlertsResponse> {
  const { from, to } = range;
  return useQuery({
    queryKey: fleetAlertsQueryKey(from, to, vehicleId, opts),
    // 17h — the list is the screen a fleet manager leaves open and returns to,
    // and returning is exactly when a stale feed matters. The global default is
    // `refetchOnWindowFocus: false`; for THIS query that default is wrong.
    refetchOnWindowFocus: true,
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ from, to });
      if (vehicleId) params.set('vehicleId', vehicleId);
      if (opts.unreadOnly) params.set('unreadOnly', 'true');
      if (opts.severity) params.set('severity', opts.severity);
      return apiJson<AlertsResponse>(`/api/alerts?${params.toString()}`, { signal });
    },
    retry: (count, error) => !isNotFound(error) && count < 2,
  });
}

export const vehicleAlertsQueryKey = (
  vehicleId: string,
  from: string,
  to: string,
  opts: AlertQueryOpts,
) => ['alerts', 'byVehicle', vehicleId, from, to, opts.unreadOnly, opts.severity ?? 'any'] as const;

export function useVehicleAlerts(
  vehicleId: string,
  range: { from: string; to: string },
  filterOpts: AlertQueryOpts,
  opts: { enabled?: boolean } = {},
): UseQueryResult<AlertsResponse> {
  const { from, to } = range;
  return useQuery({
    queryKey: vehicleAlertsQueryKey(vehicleId, from, to, filterOpts),
    // 17h — the list is the screen a fleet manager leaves open and returns to,
    // and returning is exactly when a stale feed matters. The global default is
    // `refetchOnWindowFocus: false`; for THIS query that default is wrong.
    refetchOnWindowFocus: true,
    enabled: opts.enabled ?? Boolean(vehicleId),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ from, to });
      if (filterOpts.unreadOnly) params.set('unreadOnly', 'true');
      if (filterOpts.severity) params.set('severity', filterOpts.severity);
      return apiJson<AlertsResponse>(
        `/api/vehicles/${encodeURIComponent(vehicleId)}/alerts?${params.toString()}`,
        { signal },
      );
    },
    retry: (count, error) => !isNotFound(error) && count < 2,
  });
}

/** The nav-badge count. No date range — a badge scoped to "today" would silently
 *  hide last night's alert (PLAN §4). Polled softly so a freshly-read count clears
 *  without a manual refetch elsewhere; kept cheap. */
export const unreadCountQueryKey = ['alerts', 'unread-count'] as const;

export function useUnreadAlertCount(opts: { enabled?: boolean } = {}): UseQueryResult<{ unread: number }> {
  return useQuery({
    queryKey: unreadCountQueryKey,
    enabled: opts.enabled ?? true,
    queryFn: ({ signal }) => apiJson<{ unread: number }>('/api/alerts/unread-count', { signal }),
    // A count that never refreshes becomes a badge people stop trusting; a count
    // that hammers the network on a Mogadishu connection costs money. 60 s is the
    // compromise — plus an explicit invalidate on mark-read for immediacy.
    refetchInterval: 60_000,
    retry: (count, error) => !isNotFound(error) && count < 2,
  });
}

/** Mark-read / mark-all-read (PLAN §4). `{ ids }` marks those; `{ before }` marks
 *  every alert at-or-older-than the cutoff. On success, invalidate every alerts
 *  query AND the unread count, so the badge clears and the list re-reads together
 *  — a badge that disagrees with its list is worse than no badge. */
export type MarkReadInput = { ids: string[] } | { before: string };

export function useMarkAlertsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: MarkReadInput): Promise<{ marked: number }> => {
      const res = await apiFetch('/api/alerts/read', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new ApiError(res.status, null);
      return (await res.json()) as { marked: number };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}

/**
 * 🔴 17h — KEEP THE LIST IN STEP WITH THE BADGE, WITHOUT A SECOND POLL.
 *
 * The defect Mahad hit: the badge polled every 60 s, the LIST polled never. With
 * `refetchOnWindowFocus: false` globally and no `refetchInterval`, an open feed's
 * only refresh paths were a mark-read mutation or a remount — so a crossing could
 * be written, dispatched and delivered while the list on screen showed nothing.
 *
 * WHY NOT THE SOCKET — measured, not assumed. `LiveFrameType` is
 * `'position' | 'status'`, and nothing in `modules/geofences` or `modules/push`
 * publishes to the hub. There is no alert frame to react to. Adding one means new
 * frame plumbing through the transport slice 08 hardened — a bigger change than
 * this defect justifies, recorded as a follow-up rather than smuggled in.
 *
 * WHY NOT A SECOND POLL. `useUnreadAlertCount` ALREADY polls every 60 s, app-wide,
 * because the nav badge mounts it. So the list rides the request we already pay
 * for: when the count CHANGES, invalidate `['alerts']`. Same 60 s worst case, ZERO
 * additional steady-state requests. On a Mogadishu connection that is the point.
 *
 * It invalidates on CHANGE, not on every tick — an unconditional invalidate would
 * be the second poll wearing a different hat.
 */
export function useAlertsFeedSync(): void {
  const qc = useQueryClient();
  const { data } = useUnreadAlertCount();
  const unread = data?.unread;
  const seen = useRef<number | null>(null);
  useEffect(() => {
    if (typeof unread !== 'number') return;
    if (seen.current === null) {
      // First reading is the baseline, not a change — invalidating here would
      // re-fetch a list that has only just loaded.
      seen.current = unread;
      return;
    }
    if (seen.current === unread) return;
    seen.current = unread;
    void qc.invalidateQueries({ queryKey: ['alerts'] });
  }, [unread, qc]);
}
